/**
 * PostHog Analytics — SDK init & typed event tracking
 *
 * Initializes posthog-js with proxy, person_profiles, session replay privacy.
 * Exports the singleton instance + typed tracking utilities.
 *
 * Fires alongside existing GTM/dataLayer events (see analytics.ts) — does NOT replace them.
 */

import posthog from 'posthog-js';
import { getSessionLatestUtms } from './utm-capture';

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const isEmbedded = typeof window !== 'undefined' && window !== window.top;

if (posthogKey && !isEmbedded) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_API_HOST,
    ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    cross_subdomain_cookie: import.meta.env.VITE_POSTHOG_CROSS_SUBDOMAIN_COOKIE === 'true',
  });

  // Tag every event with environment for filtering in PostHog
  posthog.register({
    environment: import.meta.env.VITE_POSTHOG_ENVIRONMENT,
    platform: 'scio3',
  });

  const sessionLatestUtms = getSessionLatestUtms();
  if (Object.keys(sessionLatestUtms).length > 0) {
    posthog.register_for_session(sessionLatestUtms);
  }
}

/**
 * True when the PostHog SDK was actually initialized (key present + not running
 * inside an iframe). When false, `posthog.onFeatureFlags` never fires, so flag
 * consumers must resolve immediately instead of waiting on a response that will
 * never arrive.
 */
export const isPosthogEnabled = Boolean(posthogKey) && !isEmbedded;

// ── Typed Event Tracking ──────────────────────────────────────────────────────

/** All PostHog event names — `object_action` snake_case convention. */
export type PostHogEventName =
  // Category 1: Authentication
  | 'user_logged_in'
  | 'user_logged_out'
  | 'user_password_reset'
  | 'mfa_enrolled'
  // Category 2: Account & Get Started
  | 'account_created'
  | 'user_signed_up'
  | 'trial_started'
  | 'get_started_step_completed'
  | 'get_started_completed'
  | 'onboarding_started'
  | 'onboarding_step_completed'
  | 'onboarding_completed'
  | 'onboarding_skipped'
  | 'onboarding_submit_failed'
  | 'welcome_screen_variant_exposure'
  // Category 3: Screen/Device Management
  | 'screen_pairing_started'
  | 'screen_paired'
  | 'screen_pairing_failed'
  | 'content_pushed'
  | 'temp_takeover_used'
  | 'screen_bulk_action'
  | 'order_devices_opened'
  // Category 4: Content Management
  | 'asset_created'
  | 'asset_previewed'
  | 'playlist_created'
  | 'playlist_updated'
  | 'schedule_created'
  | 'schedule_updated'
  // Category 5: Collaboration & Teams
  | 'team_switched'
  | 'team_member_invited'
  | 'folder_created'
  | 'api_key_created'
  // Category 6: Billing & Subscription
  | 'first_subscription'
  | 'sub_update'
  | 'winback_reactivated'
  // Category 7: AI Features
  | 'optibot_conversation_started'
  | 'optibot_message_sent'
  | 'optibot_feedback_given'
  // Category 8: Navigation & Engagement
  | 'page_viewed'
  | 'search_performed'
  | 'search_page_opened'
  | 'search_page_result_clicked'
  | 'bulk_action_performed'
  | 'export_generated'
  | 'notification_clicked'
  | 'upgrade_cta_clicked'
  // Category 9: Errors & System
  | 'error_encountered'
  | 'api_error_occurred'
  | 'help_accessed'
  // Category 10: Rooms / MDM Connect
  | 'room_connect_started'
  | 'room_connect_completed'
  | 'room_connect_failed'
  | 'room_signage_activated'
  | 'room_content_assigned'
  // Category 11: Referral (Refer & Earn)
  | 'referral_widget_shown'
  | 'referral_pill_opened'
  | 'referral_pill_dismissed'
  | 'referral_modal_auto_opened'
  | 'referral_invite_sent'
  | 'referral_link_copied'
  | 'referral_menu_clicked'
  | 'referral_page_viewed';

/**
 * Type-safe PostHog event capture. No-ops if PostHog key is not configured.
 *
 * Usage: `trackEvent('asset_created', { asset_type: 'image', is_first: true })`
 */
interface PostHogCapturedEvent {
  event: string;
  properties: Record<string, unknown>;
  timestamp: number;
}

interface WindowWithE2E extends Window {
  __posthogCapturedEvents?: PostHogCapturedEvent[];
}

function pushE2EEvent(event: string, properties: Record<string, unknown>) {
  const w = window as WindowWithE2E;
  (w.__posthogCapturedEvents ??= []).push({ event, properties, timestamp: Date.now() });
}

export function trackEvent(event: PostHogEventName, properties?: Record<string, unknown>) {
  if (!posthogKey) return;
  posthog.capture(event, properties);

  // In E2E mode, also push to a window array for Playwright assertion
  if (import.meta.env.VITE_E2E === 'true') {
    pushE2EEvent(event, properties ?? {});
  }
}

// In E2E mode, initialize the capture array and wrap identify/group for assertions
if (import.meta.env.VITE_E2E === 'true') {
  (window as WindowWithE2E).__posthogCapturedEvents = [];
}
if (import.meta.env.VITE_E2E === 'true' && posthogKey && !isEmbedded) {
  const origIdentify = posthog.identify.bind(posthog);
  posthog.identify = (distinctId: string, properties?: Record<string, unknown>) => {
    pushE2EEvent('$identify', { distinct_id: distinctId, ...properties });
    origIdentify(distinctId, properties);
  };

  const origGroup = posthog.group.bind(posthog);
  posthog.group = (type: string, key: string, properties?: Record<string, unknown>) => {
    pushE2EEvent('$groupidentify', { group_type: type, group_key: key, ...properties });
    origGroup(type, key, properties);
  };
}

// ── Identify / Group dedupe ───────────────────────────────────────────────────
//
// Two-layer dedupe for posthog.identify / posthog.group:
//   1. SDK state (get_distinct_id / getGroups) is the source of truth for
//      association — self-heals if PostHog persistence was cleared (ITP,
//      private mode, manual clear) but our cache wasn't.
//   2. Stored payload snapshot detects property changes.
// Fires when EITHER is out of sync. The snapshot is plaintext JSON, NOT a
// security hash — consistent with `ph_<token>_posthog` / `scio3-team` /
// `scio-cache-owner` which already store user-identifying fields in plaintext.

const PERSON_KEY = (uid: string) => `posthog:identify:person:${uid}`;
const GROUP_KEY = (aid: string) => `posthog:identify:group:${aid}`;

function serializePayload(obj: Record<string, unknown>): string {
  return JSON.stringify(obj, Object.keys(obj).sort());
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable / quota exceeded — fall through, next call fires
  }
}

export function shouldFireIdentify(args: {
  userId: string;
  payload: Record<string, unknown>;
  currentDistinctId: string | null | undefined;
}): boolean {
  if (import.meta.env.VITE_E2E === 'true') return true;
  const { userId, payload, currentDistinctId } = args;
  const next = serializePayload(payload);
  const sdkOutOfSync = currentDistinctId !== userId;
  const payloadChanged = safeGet(PERSON_KEY(userId)) !== next;
  if (sdkOutOfSync || payloadChanged) {
    safeSet(PERSON_KEY(userId), next);
    return true;
  }
  return false;
}

export function shouldFireGroup(args: {
  accountId: string;
  payload: Record<string, unknown>;
  currentGroups: Record<string, string> | null | undefined;
}): boolean {
  if (import.meta.env.VITE_E2E === 'true') return true;
  const { accountId, payload, currentGroups } = args;
  const next = serializePayload(payload);
  const sdkOutOfSync = currentGroups?.company !== accountId;
  const payloadChanged = safeGet(GROUP_KEY(accountId)) !== next;
  if (sdkOutOfSync || payloadChanged) {
    safeSet(GROUP_KEY(accountId), next);
    return true;
  }
  return false;
}

export function clearIdentityCache(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('posthog:identify:')) localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

export { posthog };
